import React, { useState } from 'react';
import { Mic, RotateCcw, Check, Loader2, Trash2, Upload, Link } from 'lucide-react';
import axios from 'axios';
import { resolveFileUrl } from '../../utils/fileUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AudioItem = ({ audio, index, interventionId, onRefresh }) => {
  const [retrying, setRetrying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState(null);
  const hasTranscription = audio.transcription && audio.transcription.trim().length > 0;

  const handleRetry = async () => {
    setRetrying(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/interventions/${interventionId}/audio/${index}/retry-transcription`,
        {},
        { withCredentials: true }
      );
      setResult({ ok: true, message: res.data.message });
      if (onRefresh) onRefresh();
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.detail || 'Errore trascrizione' });
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/interventions/${interventionId}/audio/${index}`,
        { withCredentials: true }
      );
      if (onRefresh) onRefresh();
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.detail || 'Errore eliminazione' });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-1" data-testid={`audio-item-${index}`}>
      <div className="audio-item">
        <Mic className="w-5 h-5 text-[#A1A1AA] shrink-0" />
        <audio controls className="flex-1 h-8">
          <source src={resolveFileUrl(audio.url)} type="audio/ogg" />
        </audio>
        {confirmDelete ? (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={handleDelete} disabled={deleting}
              className="text-[10px] uppercase tracking-wider text-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
              data-testid={`confirm-delete-audio-${index}`}>
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Sì'}
            </button>
            <button onClick={() => setConfirmDelete(false)}
              className="text-[10px] uppercase tracking-wider text-[#71717A] hover:text-white transition-colors"
              data-testid={`cancel-delete-audio-${index}`}>
              No
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)}
            className="text-[#71717A] hover:text-red-500 transition-colors shrink-0"
            data-testid={`delete-audio-${index}`}
            title="Elimina vocale">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {!hasTranscription && (
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#E3000F] hover:text-white transition-colors disabled:opacity-50 ml-7"
          data-testid={`retry-transcription-${index}`}
        >
          {retrying ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Trascrizione in corso...</>
          ) : (
            <><RotateCcw className="w-3 h-3" /> Ritenta trascrizione</>
          )}
        </button>
      )}
      {result && (
        <p className={`text-[10px] ml-7 ${result.ok ? 'text-green-500' : 'text-red-400'}`}>
          {result.ok ? <Check className="w-3 h-3 inline mr-1" /> : null}
          {result.message}
        </p>
      )}
    </div>
  );
};

export const AudioFilesSidebar = ({ audioFiles, interventionId, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [telegramLink, setTelegramLink] = useState('');
  const [importing, setImporting] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API_URL}/api/interventions/${interventionId}/audio/upload`,
        formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadResult({ ok: true, message: res.data.message });
      if (onRefresh) onRefresh();
    } catch (err) {
      setUploadResult({ ok: false, message: err.response?.data?.detail || 'Errore upload' });
    } finally {
      setUploading(false);
    }
  };

  const handleImportLink = async () => {
    if (!telegramLink.trim()) return;
    setImporting(true);
    setUploadResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/interventions/${interventionId}/import-telegram-link`,
        { link: telegramLink },
        { withCredentials: true }
      );
      setUploadResult({ ok: true, message: res.data.message });
      setTelegramLink('');
      setShowLinkInput(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      setUploadResult({ ok: false, message: err.response?.data?.detail || 'Errore importazione' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="bg-[#121212] border border-[#27272A] p-6" data-testid="audio-sidebar">
      <h3 className="text-lg font-semibold uppercase mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
        <Mic className="w-5 h-5 text-[#E3000F]" />
        Messaggi Vocali
      </h3>
      {audioFiles?.length > 0 ? (
        <div className="space-y-3">
          {audioFiles.map((audio, index) => (
            <AudioItem
              key={audio.url || `audio-${index}`}
              audio={audio}
              index={index}
              interventionId={interventionId}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      ) : (
        <p className="text-[#71717A] text-center py-4">Nessun messaggio vocale</p>
      )}

      <div className="mt-4 pt-3 border-t border-[#27272A] space-y-2">
        <label className={`cursor-pointer flex items-center justify-center gap-2 bg-[#1A1A1A] border border-[#E3000F]/30 hover:border-[#E3000F] text-[#E3000F] text-xs px-3 py-2.5 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid="upload-audio-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Trascrizione e compilazione modulo...' : 'Carica vocale (trascrizione automatica)'}
          <input type="file" accept="audio/*,.ogg,.oga,.mp3,.m4a,.webm,.wav" className="hidden" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
        </label>

        {showLinkInput ? (
          <div className="space-y-2">
            <input
              type="text"
              value={telegramLink}
              onChange={(e) => setTelegramLink(e.target.value)}
              placeholder="Incolla link Telegram (es. https://t.me/c/...)"
              className="w-full bg-[#0A0A0A] border border-[#333] text-white text-xs px-3 py-2 focus:border-[#E3000F] outline-none"
              data-testid="telegram-link-input"
            />
            <div className="flex gap-2">
              <button onClick={handleImportLink} disabled={importing || !telegramLink.trim()}
                className="flex-1 flex items-center justify-center gap-1.5 bg-[#E3000F] hover:bg-[#C5000D] text-white text-xs px-3 py-2 transition-colors disabled:opacity-50"
                data-testid="import-link-btn">
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link className="w-3 h-3" />}
                {importing ? 'Importazione...' : 'Importa'}
              </button>
              <button onClick={() => { setShowLinkInput(false); setTelegramLink(''); }}
                className="text-[#71717A] hover:text-white text-xs px-3 py-2 transition-colors">
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowLinkInput(true)}
            className="w-full flex items-center justify-center gap-2 bg-[#1A1A1A] border border-[#27272A] hover:border-[#E3000F]/50 text-[#71717A] hover:text-[#E3000F] text-xs px-3 py-2.5 transition-colors"
            data-testid="show-link-import-btn">
            <Link className="w-3.5 h-3.5" />
            Importa da link Telegram
          </button>
        )}

        {uploadResult && (
          <p className={`text-[10px] mt-2 text-center ${uploadResult.ok ? 'text-green-500' : 'text-red-400'}`}>
            {uploadResult.ok ? <Check className="w-3 h-3 inline mr-1" /> : null}
            {uploadResult.message}
          </p>
        )}
      </div>
    </div>
  );
};
